import {
  type UserRow,
  listActiveTicketCounts,
  listDepartments,
  listUsers,
} from '@/features/configuracoes/api';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { transferTicket } from '../api';
import { invalidateServerTruthLists } from '../inbox-debug';
import { getSocket } from '../socket';

interface Props {
  ticketId: number;
  /** User atualmente atribuído — escondido do select (transferir pro mesmo é no-op). */
  currentUserId?: number | null;
  /** Departamento atual — escondido do select. */
  currentDepartmentId?: number | null;
  open: boolean;
  onClose: () => void;
  onTransferred: () => void;
}

/**
 * Story 3.10 — Modal de transferência de ticket.
 *
 * Permite transferir ticket pra:
 * - Outro atendente (`user_id`)
 * - Departamento específico (`department_id`)
 * - Ou ambos (departamento + atendente do departamento)
 *
 * Backend exige pelo menos 1 dos campos. Se passar só `department_id`,
 * o ticket fica `status='active'` mas sem `user_id` — atendentes do dept podem aceitar.
 */
export function TransferModal({
  ticketId,
  currentUserId,
  currentDepartmentId,
  open,
  onClose,
  onTransferred,
}: Props) {
  const qc = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Por padrão só mostra atendentes ONLINE (conectados + disponíveis). Toggle
  // pra incluir offline cobre o caso de atribuir a quem vai voltar depois.
  const [includeOffline, setIncludeOffline] = useState(false);

  useEscapeClose(open, onClose);

  // Pré-seleciona o dept atual ao abrir — caso mais comum é só trocar atendente.
  useEffect(() => {
    if (open) {
      setSelectedDepartmentId(currentDepartmentId ?? null);
      setSelectedUserId(null);
      setError(null);
      setIncludeOffline(false);
    }
  }, [open, currentDepartmentId]);

  const usersQ = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => listUsers(),
    enabled: open,
  });

  const deptsQ = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
  });

  // Carga atual por atendente (nº de atendimentos ativos) — pra ajudar a
  // distribuir. Buscado só ao abrir o modal; snapshot é suficiente pra decidir.
  const countsQ = useQuery({
    queryKey: ['active-ticket-counts'],
    queryFn: listActiveTicketCounts,
    enabled: open,
  });
  const countByUser = useMemo(() => {
    const m = new Map<number, number>();
    for (const c of countsQ.data ?? []) m.set(c.user_id, c.count);
    return m;
  }, [countsQ.data]);

  // Empresa com 1 departamento só: se o ticket não trouxe dept de origem,
  // pré-seleciona o único departamento assim que a lista carrega — poupa o
  // clique no caso comum de empresa com um dept só. Usa functional update pra
  // só agir enquanto nada está selecionado (não briga com escolha manual do
  // usuário, que não re-dispara este effect). Se houver dept de origem, ele
  // tem prioridade (early-return).
  useEffect(() => {
    if (!open || currentDepartmentId != null) return;
    const depts = deptsQ.data ?? [];
    if (depts.length === 1) {
      const only = depts[0];
      if (only) setSelectedDepartmentId((prev) => (prev == null ? only.id : prev));
    }
  }, [open, currentDepartmentId, deptsQ.data]);

  // Presença ao vivo: se um atendente conecta/sai com o modal aberto, atualiza a
  // lista (o effect de clear abaixo tira a seleção se o escolhido ficar offline).
  useEffect(() => {
    if (!open) return;
    const socket = getSocket();
    const onPresence = (p: { user_id: number; online: boolean }): void => {
      qc.setQueryData<UserRow[]>(['settings-users'], (prev) =>
        prev?.map((u) => (u.id === p.user_id ? { ...u, online: p.online } : u)),
      );
    };
    socket.on('presence.changed', onPresence);
    return () => {
      socket.off('presence.changed', onPresence);
    };
  }, [open, qc]);

  const mut = useMutation({
    mutationFn: () => {
      const input: { user_id?: number; department_id?: number; require_online?: boolean } = {};
      if (selectedUserId) input.user_id = selectedUserId;
      if (selectedDepartmentId) input.department_id = selectedDepartmentId;
      // Só exige online quando o atendente veio da lista "só online" (toggle off).
      if (selectedUserId && !includeOffline) input.require_online = true;
      return transferTicket(ticketId, input);
    },
    onSuccess: () => {
      // PRIMEIRO limpa a seleção do thread panel — se invalidate vier antes,
      // dispara refetch de `['ticket', ticketId]` enquanto user já perdeu
      // acesso (transfer fora do dept dele) → 404 → "Erro ao carregar".
      onTransferred();
      reset();
      invalidateServerTruthLists(qc, 'transferMut');
      // NÃO invalidar ['ticket', ticketId] — após transfer, user pode ter
      // perdido acesso. Refetch geraria 404. ThreadPanel já foi pra null
      // via onTransferred → placeholder, sem consumer pra essa query.
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : err instanceof Error
            ? err.message
            : 'Falha ao transferir',
      );
    },
  });

  const reset = (): void => {
    setSelectedUserId(null);
    setSelectedDepartmentId(null);
    setError(null);
    setIncludeOffline(false);
  };

  // IMPORTANTE: todos os hooks (useMemo/useEffect) DEVEM ficar acima do
  // `if (!open) return null;` — senão React quebra com erro #310 quando o
  // modal fecha (menos hooks na render).

  // Atendentes ativos apenas (e ignora o user atribuído atualmente —
  // transferir pra si mesmo é no-op real).
  const allActiveUsers = useMemo(
    () => (usersQ.data ?? []).filter((u) => u.status === 'active' && u.id !== currentUserId),
    [usersQ.data, currentUserId],
  );

  // Departamentos: mostra TODOS (inclui o atual). Permite o caso de trocar
  // só o atendente mantendo o mesmo dept. Backend aceita transferir pro mesmo
  // dept com user_id diferente — o que era "no-op" antes era dept atual +
  // user atual, mas user atual já está filtrado em `allActiveUsers` acima.
  const availableDepts = useMemo(() => deptsQ.data ?? [], [deptsQ.data]);

  // Fluxo: dept primeiro, atendente filtrado pelo dept.
  // - Dept selecionado → apenas atendentes vinculados a ele.
  // - "Sem departamento" → ticket público, qualquer atendente pode pegar.
  const deptUsers = useMemo(() => {
    if (selectedDepartmentId === null) return allActiveUsers;
    return allActiveUsers.filter((u) => u.departments.some((d) => d.id === selectedDepartmentId));
  }, [allActiveUsers, selectedDepartmentId]);

  // Por padrão só ONLINE — quem realmente pode atender agora. Toggle inclui offline.
  const filteredUsers = useMemo(
    () => (includeOffline ? deptUsers : deptUsers.filter((u) => u.online)),
    [deptUsers, includeOffline],
  );

  // Se o atendente selecionado saiu da lista visível (mudou dept OU ficou
  // offline com o filtro ligado), limpa a seleção.
  useEffect(() => {
    if (selectedUserId == null) return;
    if (!filteredUsers.some((u) => u.id === selectedUserId)) setSelectedUserId(null);
  }, [filteredUsers, selectedUserId]);

  if (!open) return null;

  const submit = (): void => {
    setError(null);
    if (!selectedDepartmentId) {
      return setError('Selecione um departamento');
    }
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[460px] max-w-[92vw] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">Transferir atendimento</h2>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <p className="mt-2 text-[12px] text-n-500">
          Selecione o departamento de destino. Opcionalmente, escolha também um atendente vinculado
          a ele — sem atendente, o ticket fica na fila do dept.
        </p>

        <div className="mt-4 flex flex-col gap-3.5">
          <Field label="Departamento">
            <select
              value={selectedDepartmentId ?? ''}
              onChange={(e) =>
                setSelectedDepartmentId(e.target.value ? Number(e.target.value) : null)
              }
              className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
              disabled={deptsQ.isLoading || availableDepts.length === 0}
            >
              {availableDepts.length === 0 ? (
                <option value="">— Sem departamento cadastrado —</option>
              ) : (
                <>
                  <option value="">Selecione…</option>
                  {availableDepts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </Field>

          <Field label="Atendente (opcional)">
            <select
              value={selectedUserId ?? ''}
              onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
              className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={usersQ.isLoading || selectedDepartmentId === null}
            >
              <option value="">
                {selectedDepartmentId === null
                  ? '— Escolha um departamento primeiro —'
                  : '— Deixar na fila do departamento —'}
              </option>
              {selectedDepartmentId !== null &&
                filteredUsers.map((u) => {
                  const carga = countByUser.get(u.id) ?? 0;
                  return (
                    <option key={u.id} value={u.id}>
                      {u.name}
                      {u.online ? ' · online' : ' · offline'} · {carga}{' '}
                      {carga === 1 ? 'atendimento' : 'atendimentos'}
                    </option>
                  );
                })}
            </select>
          </Field>

          {selectedDepartmentId !== null && (
            <label className="-mt-1.5 flex cursor-pointer items-center gap-2 text-[11px] text-n-600">
              <input
                type="checkbox"
                checked={includeOffline}
                onChange={(e) => setIncludeOffline(e.target.checked)}
              />
              Mostrar atendentes offline
            </label>
          )}

          {error && (
            <div className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded-md border border-n-200 bg-n-0 px-4 py-2 font-semibold text-[12px] text-n-700 hover:bg-n-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={mut.isPending}
            className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Transferir
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      {children}
    </label>
  );
}
